import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  dayjsLocalizer,
  View,
  Views,
  type Event as RbcEvent,
  type ToolbarProps,
} from 'react-big-calendar';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  UploadFileOutlined as UploadFileIcon,
} from '@mui/icons-material';

import { Project } from '../../../models/Project';
import {
  AllSiteSchedule,
  EVENT_TYPE_LABEL,
  eventTypeFallback,
  hasScheduleDate,
} from '../../../models/SiteSchedule';
import MemoBoard from './MemoBoard';
import { SiteScheduleService } from '../../../services/SiteScheduleService';
import RecordFormDialog from '../../../components/dialogs/RecordFormDialog';
import { RecordKind } from '../../../components/dialogs/RecordKindSelector';
import ScheduleFormDialog, {
  canEditSchedule,
  splitAssignees,
} from '../../../components/dialogs/ScheduleFormDialog';
import ScheduleDetailDialog from '../../../pages/ProjectDashboardPage/dialogs/ScheduleDetailDialog';
import { initialsOf, avatarBgColor } from '../../../components/UserProfileMenu';
import useUserDirectory from '../../../hooks/useUserDirectory';
import { withMediaToken } from '../../../utils/media';
import { useDashboardTokens } from '../tokens';

dayjs.locale('ko');
const localizer = dayjsLocalizer(dayjs);

/**
 * 현장(site_project_id)별 색상 — 유형이 아니라 "어느 현장 일정인지" 를 한눈에
 * 구분하기 위한 팔레트. 전체 현장을 하나로 합친 캘린더라 이게 더 유용한 축.
 * 현장 수가 팔레트보다 많으면 순서대로 다시 순환한다(완전한 유일성은 보장 안 함).
 */
const SITE_COLOR_PALETTE = [
  '#1976d2', // blue
  '#e64a19', // deep orange
  '#2e7d32', // green
  '#7b1fa2', // purple
  '#00838f', // cyan
  '#c2185b', // pink
  '#f9a825', // amber
  '#455a64', // blue grey
  '#5d4037', // brown
  '#0288d1', // light blue
  '#558b2f', // olive green
  '#ad1457', // deep pink
];

function colorForSite(siteProjectId: number): string {
  if (!Number.isFinite(siteProjectId)) return SITE_COLOR_PALETTE[0];
  const idx = siteProjectId % SITE_COLOR_PALETTE.length;
  return SITE_COLOR_PALETTE[idx];
}

type Filter = 'all' | 'mine';

interface CalendarEvent extends RbcEvent {
  start: Date;
  end: Date;
  resource: AllSiteSchedule;
}

/**
 * 담당자(assignee CSV) 를 유저 디렉토리와 이름으로 매칭해 프로필 이미지(없으면
 * 이니셜) 아바타로 표시. 여러 명이면 겹쳐서 나열. 매칭 실패(퇴사자·오탈자 등)해도
 * 이니셜 아바타로 안전하게 fallback.
 */
const AssigneeAvatars: React.FC<{ assignee?: string; size?: number }> = ({ assignee, size = 20 }) => {
  const { users } = useUserDirectory();
  const names = splitAssignees(assignee);
  if (names.length === 0) return null;
  const userByName = new Map(users.map((u) => [u.name, u]));
  return (
    <Stack direction="row" spacing={-0.75} sx={{ flexShrink: 0 }}>
      {names.map((name) => {
        const u = userByName.get(name);
        const src = u?.profile_image_url ? withMediaToken(u.profile_image_url) : undefined;
        return (
          <Tooltip key={name} title={`담당: ${name}`}>
            <Avatar
              src={src}
              sx={{
                width: size,
                height: size,
                fontSize: size * 0.42,
                fontWeight: 700,
                bgcolor: avatarBgColor(name),
                color: '#fff',
                border: '1.5px solid',
                borderColor: 'background.paper',
              }}
            >
              {initialsOf(name)}
            </Avatar>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

/**
 * 월/주/일 뷰의 일정 블록 렌더러 — 기본 텍스트만 나오던 것에 담당자 아바타를 추가.
 */
const CompactEvent: React.FC<{ event: CalendarEvent }> = ({ event }) => {
  const s = event.resource;
  const type = eventTypeFallback(s.event_type);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <AssigneeAvatars assignee={s.assignee} size={14} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {s.site_name} · {EVENT_TYPE_LABEL[type]} · {s.title}
      </span>
    </Stack>
  );
};

/**
 * 목록(agenda) 뷰의 일정 셀 렌더러. 행 배경은 흰색(가독성)으로 두고, 현장은
 * 색상 칩(pill)으로 또렷하게 표시한다(어느 현장인지가 주 구분축). 유형은 색
 * 없는 보조 텍스트로만, 제목은 그 다음 위계. 색은 inherit 기반(opacity)이라
 * 라이트/다크 모드 모두 안전.
 */
const AgendaEvent: React.FC<{ event: CalendarEvent }> = ({ event }) => {
  const s = event.resource;
  const type = eventTypeFallback(s.event_type);
  const color = colorForSite(s.site_project_id);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span
        style={{
          flex: '0 0 auto',
          backgroundColor: color,
          color: '#fff',
          fontSize: '0.68rem',
          fontWeight: 700,
          lineHeight: 1.7,
          padding: '0 9px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
          letterSpacing: '0.02em',
        }}
      >
        {s.site_name}
      </span>
      <span style={{ opacity: 0.6, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
        {EVENT_TYPE_LABEL[type]}
      </span>
      <span style={{ opacity: 0.55 }}>·</span>
      <span style={{ opacity: 0.78, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {s.title}
      </span>
      <AssigneeAvatars assignee={s.assignee} size={20} />
    </span>
  );
};

/**
 * 커스텀 툴바 — 기본 "이전/다음" 텍스트 버튼 대신 라벨("7월 2026") 좌우에
 * 화살표 아이콘을 붙여 같은 자리에서 월 이동이 되도록 한다.
 */
const CalendarToolbar: React.FC<ToolbarProps<CalendarEvent>> = ({ label, view, onNavigate, onView }) => {
  const tokens = useDashboardTokens();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      rowGap={1}
      sx={{ mb: 1.5 }}
    >
      <Button size="small" variant="outlined" onClick={() => onNavigate('TODAY')}>
        오늘
      </Button>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton size="small" onClick={() => onNavigate('PREV')} aria-label="이전 달">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ color: tokens.colors.text, minWidth: 108, textAlign: 'center' }}
        >
          {label}
        </Typography>
        <IconButton size="small" onClick={() => onNavigate('NEXT')} aria-label="다음 달">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        {CALENDAR_VIEWS.map((v) => (
          <Button
            key={v}
            size="small"
            variant={view === v ? 'contained' : 'outlined'}
            onClick={() => onView(v)}
          >
            {KO_MESSAGES[v as keyof typeof KO_MESSAGES] as string}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
};

/** Calendar components — 모듈 레벨 상수로 두어 리렌더 시 동일 참조 유지. */
const CALENDAR_COMPONENTS = { event: CompactEvent, agenda: { event: AgendaEvent }, toolbar: CalendarToolbar };

const KO_MESSAGES = {
  date: '날짜',
  time: '시간',
  event: '일정',
  allDay: '종일',
  week: '주',
  work_week: '주중',
  day: '일',
  month: '월',
  previous: '이전',
  next: '다음',
  yesterday: '어제',
  tomorrow: '내일',
  today: '오늘',
  agenda: '목록',
  noEventsInRange: '이 기간에 일정이 없습니다.',
  showMore: (total: number) => `+${total}개 더보기`,
};

const CALENDAR_VIEWS: View[] = [Views.MONTH, Views.AGENDA];

/** date + view 로 조회할 날짜 범위(from/to ISO)를 넉넉하게 계산. */
function visibleRange(date: Date, view: View): { from: string; to: string } {
  const d = dayjs(date);
  let start: dayjs.Dayjs;
  let end: dayjs.Dayjs;
  switch (view) {
    case Views.DAY:
      start = d.startOf('day').subtract(1, 'day');
      end = d.endOf('day').add(1, 'day');
      break;
    case Views.WEEK:
      start = d.startOf('week').subtract(1, 'day');
      end = d.endOf('week').add(1, 'day');
      break;
    case Views.AGENDA:
      start = d.startOf('day');
      end = d.add(31, 'day').endOf('day');
      break;
    case Views.MONTH:
    default:
      // 월 뷰는 앞뒤 주가 보이므로 ±7일 패딩.
      start = d.startOf('month').subtract(7, 'day');
      end = d.endOf('month').add(7, 'day');
      break;
  }
  return { from: start.toISOString(), to: end.toISOString() };
}

interface ScheduleCalendarTabProps {
  /** 일정 등록 시 현장 선택용 — 전체 현장 목록. */
  sites: Project[];
}

/**
 * 일정 캘린더 탭 — 전체 현장 통합 일정을 월/목록 뷰로 표시.
 *
 *  - 필터: 현장(전체/단일, 클라이언트 필터) · 전체/내 일정(작성자 == 나, 서버 필터)
 *  - 이벤트 클릭 → 상세 보기 모달(ScheduleDetailDialog). 쓰기 권한자는 "수정"
 *    버튼으로 편집 다이얼로그 전환.
 *  - 빈 슬롯 클릭(admin/editor) → 해당 시각으로 일정 등록 다이얼로그
 *  - 날짜를 이어 드래그(admin/editor) → 그 기간(시작·종료)이 채워진 일정 등록 다이얼로그
 */
const ScheduleCalendarTab: React.FC<ScheduleCalendarTabProps> = ({ sites }) => {
  const theme = useTheme();
  const tokens = useDashboardTokens();
  const writable = useMemo(() => canEditSchedule(), []);

  const [date, setDate] = useState<Date>(new Date());
  const [view, setView] = useState<View>(Views.MONTH);
  const [filter, setFilter] = useState<Filter>('all');
  // 'all' = 전체 현장, 그 외에는 site_project_id 문자열 — 캘린더에 해당 현장 일정만 표시.
  const [siteFilter, setSiteFilter] = useState<string>('all');
  // 목록(agenda) 뷰 전용 사용자 지정 기간. 기본 오늘 ~ +30일.
  const [agendaStart, setAgendaStart] = useState<Date>(() => dayjs().startOf('day').toDate());
  const [agendaEnd, setAgendaEnd] = useState<Date>(() => dayjs().add(30, 'day').endOf('day').toDate());
  const [schedules, setSchedules] = useState<AllSiteSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  /**
   * 어떤 등록 폼을 열지. 버튼이 곧 종류다 — 업무 등록 / 파일 등록.
   *
   * 예전에는 버튼 하나를 열고 다이얼로그 안에서 종류를 골랐는데(무엇을 남기나요),
   * 실제로 쓰는 건 업무와 파일 둘뿐이라 고르는 단계가 그대로 군더더기가 됐다.
   * 이제 버튼에서 정하고 폼은 바로 뜬다.
   */
  const [createKind, setCreateKind] = useState<RecordKind>('schedule');
  const [presetLocal, setPresetLocal] = useState<string | undefined>(undefined);
  // 월간뷰에서 날짜를 이어 드래그했을 때의 종료 일시 프리셋.
  const [presetEndLocal, setPresetEndLocal] = useState<string | undefined>(undefined);
  // 일정 클릭 시 바로 내용을 보여주는 상세 모달, "수정" 클릭 시 편집 다이얼로그로 전환.
  const [detailSchedule, setDetailSchedule] = useState<AllSiteSchedule | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<AllSiteSchedule | null>(null);

  const isAgenda = view === Views.AGENDA;

  // 목록 뷰는 사용자 지정 기간을, 그 외 뷰는 현재 보이는 범위를 조회.
  const { from, to } = useMemo(() => {
    if (view === Views.AGENDA) {
      return {
        from: dayjs(agendaStart).startOf('day').toISOString(),
        to: dayjs(agendaEnd).endOf('day').toISOString(),
      };
    }
    return visibleRange(date, view);
  }, [date, view, agendaStart, agendaEnd]);

  // agenda 가 표시할 일수(시작·종료 포함) — react-big-calendar Agenda 의 length prop.
  const agendaLength = useMemo(
    () => Math.max(1, dayjs(agendaEnd).startOf('day').diff(dayjs(agendaStart).startOf('day'), 'day') + 1),
    [agendaStart, agendaEnd],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    SiteScheduleService.listAll(from, to, filter === 'mine')
      .then((list) => {
        if (!cancelled) setSchedules(list);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '일정 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, filter, refreshKey]);

  // 현장 필터 — 서버는 항상 전체 현장 일정을 내려주므로 클라이언트에서 걸러낸다.
  const visibleSchedules = useMemo(
    () =>
      siteFilter === 'all'
        ? schedules
        : schedules.filter((s) => String(s.site_project_id) === siteFilter),
    [schedules, siteFilter],
  );

  const events = useMemo<CalendarEvent[]>(() => {
    return visibleSchedules
      .map((s) => {
        // 시점 없는 메모는 캘린더에 자리가 없다. new Date(null) 은 NaN 이 아니라
        // epoch(1970-01-01) 이므로 아래 NaN 검사만으로는 걸러지지 않는다.
        if (!hasScheduleDate(s)) return null;
        const start = new Date(s.scheduled_at as string);
        if (Number.isNaN(start.getTime())) return null;
        const end = s.scheduled_end_at ? new Date(s.scheduled_end_at) : new Date(start.getTime() + 60 * 60 * 1000);
        const type = eventTypeFallback(s.event_type);
        return {
          title: `${s.site_name} · ${EVENT_TYPE_LABEL[type]} · ${s.title}`,
          start,
          end: Number.isNaN(end.getTime()) ? start : end,
          resource: s,
        } as CalendarEvent;
      })
      .filter((e): e is CalendarEvent => e !== null);
  }, [visibleSchedules]);

  // 현재 로드된 일정에 실제로 등장하는 현장만 범례로 — 처음 등장한 순서 유지.
  const siteLegend = useMemo(() => {
    const seen = new Map<number, string>();
    for (const s of visibleSchedules) {
      if (!seen.has(s.site_project_id)) seen.set(s.site_project_id, s.site_name);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [visibleSchedules]);

  const eventPropGetter = useCallback(
    (event: CalendarEvent) => {
      const s = event.resource;
      const muted = s.status === 'done' || s.status === 'cancelled';
      const lineThrough = s.status === 'cancelled' ? 'line-through' : 'none';
      // 목록(agenda) 뷰는 eventPropGetter 가 <tr> 전체에 적용돼 행 전체가
      // 색으로 칠해진다(가독성 저하). 배경을 비워 흰 행으로 두고, 현장 색은
      // 일정 셀 안의 pill(AgendaEvent)로만 표시.
      if (view === Views.AGENDA) {
        return {
          style: {
            backgroundColor: 'transparent',
            opacity: muted ? 0.5 : 1,
            textDecoration: lineThrough,
          },
        };
      }
      // 월/주/일 뷰의 이벤트 블록 — 기본 외형(색/모서리/그림자/좌측 악센트)은
      // 인라인으로, hover/focus 상호작용은 아래 sx 의 .rbc-event CSS 로 처리.
      // 색은 유형이 아니라 현장(site_project_id) 기준.
      return {
        style: {
          backgroundColor: colorForSite(s.site_project_id),
          border: 'none',
          borderLeft: '3px solid rgba(255,255,255,0.55)',
          borderRadius: 6,
          padding: '1px 6px',
          opacity: muted ? 0.5 : 1,
          textDecoration: lineThrough,
          fontSize: '0.72rem',
          fontWeight: 600,
          // box-shadow 는 인라인이 아니라 sx 의 .rbc-event 에 둔다.
          // 인라인이면 specificity 가 높아 :hover 의 그림자 강조가 먹히지 않음.
        },
      };
    },
    [view],
  );

  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setDetailSchedule(event.resource);
  }, []);

  const handleSelectSlot = useCallback(
    (slot: { start: Date; slots?: Date[] }) => {
      if (!writable) return;
      const days = slot.slots ?? [];
      if (days.length > 1) {
        // 날짜를 이어 드래그 — 그 기간(첫날 09:00 ~ 마지막 날 18:00)으로
        // 시작·종료가 채워진 일정 등록 모달.
        setPresetLocal(dayjs(days[0]).hour(9).minute(0).format('YYYY-MM-DDTHH:mm'));
        setPresetEndLocal(dayjs(days[days.length - 1]).hour(18).minute(0).format('YYYY-MM-DDTHH:mm'));
      } else {
        setPresetLocal(dayjs(slot.start).format('YYYY-MM-DDTHH:mm'));
        setPresetEndLocal(undefined);
      }
      setCreateKind('schedule');
      setCreateOpen(true);
    },
    [writable],
  );

  // agenda 뷰의 prev/next/today 는 사용자 지정 창(window)을 같은 길이로 이동.
  // 그 외 뷰는 기존대로 date 만 갱신.
  const handleNavigate = useCallback(
    (newDate: Date) => {
      if (view === Views.AGENDA) {
        const span = dayjs(agendaEnd).startOf('day').diff(dayjs(agendaStart).startOf('day'), 'day');
        setAgendaStart(dayjs(newDate).startOf('day').toDate());
        setAgendaEnd(dayjs(newDate).add(span, 'day').endOf('day').toDate());
      } else {
        setDate(newDate);
      }
    },
    [view, agendaStart, agendaEnd],
  );

  // 기간 빠른 설정 — 오늘부터 n일(시작·종료 포함).
  const applyDayPreset = useCallback((days: number) => {
    const start = dayjs().startOf('day');
    setAgendaStart(start.toDate());
    setAgendaEnd(start.add(days - 1, 'day').endOf('day').toDate());
  }, []);

  // 시작일 변경 — 종료일이 시작일보다 앞서면 종료일을 시작일로 당김.
  const handleStartChange = useCallback((value: string) => {
    if (!value) return;
    const next = dayjs(value).startOf('day');
    setAgendaStart(next.toDate());
    setAgendaEnd((prev) => (dayjs(prev).isBefore(next) ? next.endOf('day').toDate() : prev));
  }, []);

  // 종료일 변경 — 시작일이 종료일보다 뒤면 시작일을 종료일로 당김.
  const handleEndChange = useCallback((value: string) => {
    if (!value) return;
    const next = dayjs(value).endOf('day');
    setAgendaEnd(next.toDate());
    setAgendaStart((prev) => (next.isBefore(dayjs(prev)) ? next.startOf('day').toDate() : prev));
  }, []);

  const isDark = tokens.mode === 'dark';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          일정 캘린더
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          sx={{ minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="all">전체 현장</MenuItem>
          {sites.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>
              {s.name_ko || s.name}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          value={filter}
          exclusive
          size="small"
          onChange={(_, v) => v && setFilter(v as Filter)}
        >
          <ToggleButton value="all" sx={{ px: 1.5, py: 0.4, fontSize: '0.75rem' }}>
            전체 일정
          </ToggleButton>
          <ToggleButton value="mine" sx={{ px: 1.5, py: 0.4, fontSize: '0.75rem' }}>
            내 일정
          </ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="새로고침">
          <span>
            <IconButton size="small" onClick={() => setRefreshKey((k) => k + 1)} disabled={loading} aria-label="새로고침">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {/* 업무 / 파일 — 버튼이 곧 종류다. 다이얼로그 안에서 다시 고르게 하지
            않는다(선택 단계가 하나 늘 뿐, 고를 것은 어차피 이 둘이었다). */}
        {writable && (
          <>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setPresetLocal(undefined);
                setPresetEndLocal(undefined);
                setCreateKind('schedule');
                setCreateOpen(true);
              }}
              disableElevation
            >
              업무 등록
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => {
                setPresetLocal(undefined);
                setPresetEndLocal(undefined);
                setCreateKind('file');
                setCreateOpen(true);
              }}
              disableElevation
            >
              파일 등록
            </Button>
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 현장별 범례 — 색은 유형이 아니라 현장 기준. */}
      {siteLegend.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}>
          {siteLegend.map(({ id, name }) => (
            <Stack key={id} direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colorForSite(id) }} />
              <Typography variant="caption" color="text.secondary">
                {name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/* 목록(agenda) 뷰 전용 — 기간을 직접 지정해 그 범위 전체를 한눈에. */}
      {isAgenda && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Typography variant="body2" fontWeight={700}>
              기간 지정
            </Typography>
            <TextField
              type="date"
              size="small"
              label="시작일"
              value={dayjs(agendaStart).format('YYYY-MM-DD')}
              onChange={(e) => handleStartChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 168 }}
            />
            <Typography variant="body2" color="text.secondary">
              ~
            </Typography>
            <TextField
              type="date"
              size="small"
              label="종료일"
              value={dayjs(agendaEnd).format('YYYY-MM-DD')}
              onChange={(e) => handleEndChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 168 }}
            />
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Button size="small" variant="outlined" onClick={() => applyDayPreset(7)} sx={{ textTransform: 'none' }}>
                7일
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyDayPreset(30)} sx={{ textTransform: 'none' }}>
                30일
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyDayPreset(90)} sx={{ textTransform: 'none' }}>
                90일
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setAgendaStart(dayjs().startOf('year').toDate());
                  setAgendaEnd(dayjs().endOf('year').toDate());
                }}
                sx={{ textTransform: 'none' }}
              >
                올해
              </Button>
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {agendaLength}일 · {events.length}건
            </Typography>
          </Stack>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          position: 'relative',
          // react-big-calendar 의 기본 흰색 테마를 MUI 다크모드와 맞춤.
          '& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view': {
            borderColor: tokens.colors.border,
          },
          '& .rbc-header, & .rbc-day-bg, & .rbc-month-row, & .rbc-time-content, & .rbc-time-header-content, & .rbc-timeslot-group, & .rbc-day-slot, & .rbc-agenda-view table': {
            borderColor: tokens.colors.border,
          },
          '& .rbc-header': { color: tokens.colors.muted, py: 0.5 },
          '& .rbc-button-link': { color: tokens.colors.text },
          '& .rbc-off-range-bg': { backgroundColor: isDark ? '#0f0f12' : '#f4f4f5' },
          '& .rbc-today': {
            backgroundColor: isDark ? 'rgba(91,91,255,0.12)' : 'rgba(48,48,255,0.06)',
          },
          // 날짜 드래그(기간 선택) 중 셀 강조 — rbc 기본값은 반투명 '검정'이라
          // 다크모드에서는 오히려 더 어두워져 선택이 안 보인다. accent 색으로.
          '& .rbc-day-bg.rbc-selected-cell': {
            backgroundColor: isDark ? 'rgba(91,91,255,0.35)' : 'rgba(48,48,255,0.14)',
          },
          '& .rbc-agenda-view table tbody > tr > td': { color: tokens.colors.text },
          '& .rbc-show-more': { color: tokens.colors.accent, backgroundColor: 'transparent' },

          // ── 월간뷰: 주 행 높이를 그 주 일정 수만큼 늘려 "+N개 더보기" 로 잘리지
          // 않고 전부 펼쳐서 보이게 한다. 기본은 모든 주가 flex:1 로 동일 높이를
          // 나눠 갖고 overflow:hidden 으로 넘치는 일정을 "+N개 더보기" 로 접는데,
          // 여기서는 각 행을 컨텐츠 높이에 맞춰(flex:0 0 auto) 늘어나게 하고
          // showAllEvents(Calendar prop)로 JS 쪽 개수 제한(rowLimit)도 끈다.
          '& .rbc-month-view': { flex: '0 0 auto', height: 'auto' },
          '& .rbc-month-row': {
            flex: '0 0 auto',
            height: 'auto',
            minHeight: 110,
            overflow: 'visible',
          },
          '& .rbc-row-content-scrollable': { height: 'auto' },
          '& .rbc-row-content-scrollable .rbc-row-content-scroll-container': {
            height: 'auto',
            maxHeight: 'none',
            overflowY: 'visible',
          },

          // ── 이벤트 블록(월/주/일) 상호작용 ──────────────────────────────
          // 마우스를 올리면 살짝 밝아지며 떠오르듯 보여 "선택 가능"함을 직관적으로 전달.
          '& .rbc-event': {
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.28)',
            transition: 'filter 140ms ease, box-shadow 140ms ease, transform 140ms ease',
            outline: 'none',
          },
          '& .rbc-event:hover': {
            filter: 'brightness(1.14) saturate(1.05)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.32)',
            transform: 'translateY(-1px)',
            zIndex: 6,
          },
          '& .rbc-event:focus-visible': {
            boxShadow: `0 0 0 2px ${tokens.colors.accent}`,
            filter: 'brightness(1.1)',
          },
          '& .rbc-event.rbc-selected': {
            boxShadow: `0 0 0 2px ${tokens.colors.accent}, 0 4px 12px rgba(0,0,0,0.32)`,
            filter: 'brightness(1.08)',
          },
          '& .rbc-event-label': { fontSize: '0.66rem', opacity: 0.85, fontWeight: 500 },
          '& .rbc-event-content': { fontWeight: 600, lineHeight: 1.25 },
          // 월 뷰의 점(dot) 표기형 이벤트도 hover 시 배경 강조.
          '& .rbc-day-slot .rbc-event': { borderRadius: 6 },

          // ── 목록(agenda) 행 hover ──────────────────────────────────────
          '& .rbc-agenda-view tbody > tr': {
            transition: 'background-color 120ms ease',
            cursor: 'pointer',
          },
          '& .rbc-agenda-view tbody > tr:hover > td': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.5)',
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        <Calendar
          localizer={localizer}
          events={events}
          date={isAgenda ? agendaStart : date}
          view={view}
          views={CALENDAR_VIEWS}
          length={agendaLength}
          onNavigate={handleNavigate}
          onView={setView}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable={writable}
          popup
          // 월간뷰의 "+N개 더보기" 축약을 끄고 그 주의 일정을 전부 렌더링한다
          // (위 .rbc-month-row 등 CSS 오버라이드와 함께 동작 — 행이 자동으로 늘어남).
          showAllEvents
          messages={KO_MESSAGES}
          eventPropGetter={eventPropGetter}
          components={CALENDAR_COMPONENTS}
          // 월간뷰는 내용(일정 수)만큼 늘어나야 하므로 고정 높이를 주지 않는다.
          // 주/일/목록 뷰는 시간대 그리드·목록이라 기존처럼 고정 높이 + 내부 스크롤 유지.
          style={{ height: view === Views.MONTH ? 'auto' : '70vh' }}
        />
      </Paper>

      {/* 날짜 없는 메모는 기간 조회(from/to)에 걸리지 않아 위 캘린더에 절대
          나타나지 않는다 — 여기서 별도로 보여 준다. */}
      <MemoBoard sites={sites} writable={writable} refreshKey={refreshKey} />

      {/* 종류는 버튼에서 이미 정해졌다 — kinds 를 하나로 좁히면 다이얼로그의
          종류 선택기가 뜨지 않는다. */}
      <RecordFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialKind={createKind}
        kinds={[createKind]}
        selectableSites={sites}
        presetStart={presetLocal ? new Date(presetLocal) : null}
        presetEnd={presetEndLocal ? new Date(presetEndLocal) : null}
        onSaved={() => {
          setCreateOpen(false);
          setRefreshKey((k) => k + 1);
        }}
      />

      <ScheduleDetailDialog
        open={!!detailSchedule}
        schedule={detailSchedule}
        onClose={() => setDetailSchedule(null)}
        onEdit={
          writable
            ? (s) => {
                setDetailSchedule(null);
                setEditingSchedule(s as AllSiteSchedule);
              }
            : undefined
        }
      />

      <ScheduleFormDialog
        open={!!editingSchedule}
        editing={editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSaved={() => {
          setEditingSchedule(null);
          setRefreshKey((k) => k + 1);
        }}
        // 전체 현장 통합 캘린더이므로 수정 시에도 현장을 바꿀 수 있게
        // selectableSites 로 넘긴다(고정 siteName/projectId 대신).
        selectableSites={sites}
      />
    </Box>
  );
};

export default ScheduleCalendarTab;
